// ============================================================
// TaskFlow OS - Auth Page (Login / Register)
// ============================================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../stores/authStore';
import { IconTaskFlowLogo } from '../icons/SvgIcons';

const Auth: React.FC = () => {
  const navigate = useNavigate();
  const { login, register } = useAuthStore();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(email, password);
        if (result.error) { setError(result.error); return; }
      } else {
        if (!displayName.trim()) { setError('表示名を入力してください'); return; }
        const result = await register(email, password, displayName);
        if (result.error) { setError(result.error); return; }
      }
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      <div className="bg-particles" />

      {/* Decorative orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-emerald-500/10 blur-[100px] animate-float" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-blue-500/10 blur-[100px] animate-float" style={{ animationDelay: '3s' }} />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        className="w-full max-w-md relative z-10"
      >
        <div
          className="rounded-3xl overflow-hidden p-8"
          style={{
            background: 'rgba(255, 255, 255, 0.04)',
            backdropFilter: 'blur(30px) saturate(1.6)',
            WebkitBackdropFilter: 'blur(30px) saturate(1.6)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: `
              inset 0 1px 0 0 rgba(255, 255, 255, 0.1),
              inset 0 -2px 12px 0 rgba(0, 0, 0, 0.2),
              0 24px 80px -12px rgba(0, 0, 0, 0.4)
            `,
          }}
        >
          {/* Refraction */}
          <div className="absolute top-0 left-0 right-0 h-1/3 pointer-events-none rounded-t-3xl"
            style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, transparent 100%)' }} />

          {/* Logo */}
          <div className="flex flex-col items-center mb-8 relative z-10">
            <IconTaskFlowLogo size={48} />
            <h1 className="text-xl font-bold text-gradient mt-3">TaskFlow OS</h1>
            <p className="text-xs text-white/40 mt-1">社内タスク管理システム</p>
          </div>

          {/* Tab */}
          <div className="flex gap-1 p-1 rounded-xl bg-white/5 mb-6 relative z-10">
            <button
              onClick={() => { setIsLogin(true); setError(''); }}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${isLogin ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' : 'text-white/40'}`}
            >
              ログイン
            </button>
            <button
              onClick={() => { setIsLogin(false); setError(''); }}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${!isLogin ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' : 'text-white/40'}`}
            >
              新規登録
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4 relative z-10">
            {!isLogin && (
              <div>
                <label className="block text-xs text-white/50 mb-1.5">表示名</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="山田 太郎"
                  className="glass-input"
                  required={!isLogin}
                />
              </div>
            )}

            <div>
              <label className="block text-xs text-white/50 mb-1.5">メールアドレス</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@company.co.jp"
                className="glass-input"
                required
              />
            </div>

            <div>
              <label className="block text-xs text-white/50 mb-1.5">パスワード</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="8文字以上"
                className="glass-input"
                required
                minLength={8}
              />
            </div>

            {error && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                <p className="text-xs text-red-400">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="glass-btn-primary w-full py-3 text-sm font-semibold disabled:opacity-50"
            >
              {loading ? '処理中...' : isLogin ? 'ログイン' : '新規登録'}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default Auth;
